import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';
import { useAdminToast } from '../../hooks/useAdminToast';
import {
  Users, UserPlus, Edit2, ChevronDown, ChevronRight,
  Shield, BarChart3, Trash2
} from 'lucide-react';

// Import refactored components
import {
  StatCard,
  getRoleColor,
  TeamList,
  StatsView,
  SubordinateAdminsList,
  CreateTeamMemberModal,
  EditTeamMemberModal,
  DeleteConfirmationModal
} from './components/team';

const AdminTeam = () => {
  const [team, setTeam] = useState([]);
  const [managerStats, setManagerStats] = useState([]);
  const [adminStats, setAdminStats] = useState([]);
  const [distributionStats, setDistributionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deletingMember, setDeletingMember] = useState(null);
  const [activeTab, setActiveTab] = useState('hierarchy');
  const [expandedAdmins, setExpandedAdmins] = useState({});
  const { user } = useAuth();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const { toast } = useAdminToast();

  useEffect(() => {
    loadTeam();
    loadStats();
  }, []);

  const loadTeam = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getTeam();
      setTeam(res.data || []);
    } catch (err) {
      console.error('Failed to load team:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const [managerRes, distributionRes] = await Promise.all([
        adminAPI.getManagerStats(),
        adminAPI.getClientDistributionStats()
      ]);
      
      setManagerStats(managerRes.data?.managers || []);
      setDistributionStats(distributionRes.data);
      
      if (isSuperAdmin) {
        try {
          const adminRes = await adminAPI.getAdminStats();
          setAdminStats(adminRes.data?.admins || []);
        } catch (err) {
          console.error('Failed to load admin stats:', err);
        }
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleDeleteMember = async (memberId) => {
    try {
      const res = await adminAPI.deleteTeamMember(memberId);
      toast.success(`${res.data.message}. Клиентов откреплено: ${res.data.clients_unassigned}`);
      setDeletingMember(null);
      loadTeam();
      loadStats();
    } catch (err) {
      toast.apiError(err);
    }
  };

  // Build recursive admin hierarchy tree
  const buildAdminTree = (adminId, allAdmins, allManagers, visited = new Set()) => {
    if (visited.has(adminId)) return null;
    visited.add(adminId);
    
    const admin = allAdmins.find(a => a.id === adminId);
    if (!admin) return null;
    
    const managers = allManagers.filter(m => m.parent_admin_id === adminId);
    const subordinateAdmins = allAdmins
      .filter(a => a.parent_admin_id === adminId)
      .map(sa => buildAdminTree(sa.id, allAdmins, allManagers, visited))
      .filter(Boolean);
    
    return {
      ...admin,
      managers,
      subordinate_admins: subordinateAdmins
    };
  };

  const getTeamHierarchy = () => {
    const admins = team.filter(m => m.role === 'admin');
    const managers = team.filter(m => m.role === 'manager');
    const support = team.filter(m => m.role === 'support');
    const unassignedManagers = managers.filter(m => !m.parent_admin_id);
    
    const topLevelAdmins = admins.filter(a => !a.parent_admin_id);
    const adminTrees = topLevelAdmins.map(admin => 
      buildAdminTree(admin.id, admins, managers)
    ).filter(Boolean);
    
    const allAdminIds = admins.map(a => a.id);
    const orphanedAdmins = admins.filter(a => 
      a.parent_admin_id && !allAdminIds.includes(a.parent_admin_id)
    );
    
    return {
      admins: adminTrees,
      unassignedManagers,
      support,
      orphanedAdmins
    };
  };

  const hierarchy = getTeamHierarchy();

  const totalClients = team.reduce((sum, m) => sum + (m.client_count || 0), 0);
  const totalAdmins = team.filter(m => m.role === 'admin').length;
  const totalManagers = team.filter(m => m.role === 'manager').length;
  const totalSupport = team.filter(m => m.role === 'support').length;

  const toggleAdminExpand = (adminId) => {
    setExpandedAdmins(prev => ({ ...prev, [adminId]: !prev[adminId] }));
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
            Команда
          </h1>
          <p style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
            Управление сотрудниками и их иерархией
          </p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: COLORS.primary,
              border: 'none',
              borderRadius: '8px',
              color: '#000',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            <UserPlus size={18} />
            Добавить сотрудника
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <StatCard icon={Shield} label="Админы" value={totalAdmins} color="#8b5cf6" />
        <StatCard icon={Users} label="Менеджеры" value={totalManagers} color="#3b82f6" />
        <StatCard icon={BarChart3} label="Поддержка" value={totalSupport} color="#14b8a6" />
        <StatCard icon={Users} label="Всего клиентов" value={totalClients} color={COLORS.primary} />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {['hierarchy', 'team', 'stats'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === tab ? COLORS.primary : COLORS.background,
              border: `1px solid ${activeTab === tab ? COLORS.primary : COLORS.border}`,
              borderRadius: '8px',
              color: activeTab === tab ? '#000' : COLORS.text,
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {tab === 'hierarchy' ? 'Иерархия' : tab === 'team' ? 'Все сотрудники' : 'Статистика'}
          </button>
        ))}
      </div>

      {activeTab === 'hierarchy' ? (
        <HierarchyView 
          hierarchy={hierarchy}
          expandedAdmins={expandedAdmins}
          toggleAdminExpand={toggleAdminExpand}
          isSuperAdmin={isSuperAdmin}
          setEditingMember={setEditingMember}
          setDeletingMember={setDeletingMember}
        />
      ) : activeTab === 'team' ? (
        <TeamList 
          team={team} 
          loading={loading} 
          user={user}
          isSuperAdmin={isSuperAdmin}
          onEdit={setEditingMember}
          onDelete={setDeletingMember}
        />
      ) : (
        <StatsView 
          managerStats={managerStats} 
          adminStats={adminStats}
          distributionStats={distributionStats}
          isSuperAdmin={isSuperAdmin}
        />
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateTeamMemberModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); loadTeam(); }}
        />
      )}
      
      {editingMember && (
        <EditTeamMemberModal
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onSuccess={() => { setEditingMember(null); loadTeam(); }}
        />
      )}

      {deletingMember && (
        <DeleteConfirmationModal
          member={deletingMember}
          onClose={() => setDeletingMember(null)}
          onConfirm={() => handleDeleteMember(deletingMember.id)}
        />
      )}
    </div>
  );
};

// Hierarchy View Component (kept in main file for simplicity)
const HierarchyView = ({ 
  hierarchy, 
  expandedAdmins, 
  toggleAdminExpand, 
  isSuperAdmin, 
  setEditingMember, 
  setDeletingMember 
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    {/* Admins with their managers */}
    {hierarchy.admins.map(admin => (
      <AdminCard 
        key={admin.id}
        admin={admin}
        expandedAdmins={expandedAdmins}
        toggleAdminExpand={toggleAdminExpand}
        isSuperAdmin={isSuperAdmin}
        setEditingMember={setEditingMember}
        setDeletingMember={setDeletingMember}
      />
    ))}

    {/* Unassigned Managers */}
    {hierarchy.unassignedManagers.length > 0 && (
      <UnassignedManagersCard 
        managers={hierarchy.unassignedManagers}
        isSuperAdmin={isSuperAdmin}
        setEditingMember={setEditingMember}
        setDeletingMember={setDeletingMember}
      />
    )}

    {/* Support Team */}
    {hierarchy.support.length > 0 && (
      <SupportTeamCard 
        support={hierarchy.support}
        isSuperAdmin={isSuperAdmin}
        setEditingMember={setEditingMember}
        setDeletingMember={setDeletingMember}
      />
    )}
  </div>
);

// Admin Card Component
const AdminCard = ({ admin, expandedAdmins, toggleAdminExpand, isSuperAdmin, setEditingMember, setDeletingMember }) => (
  <div style={{
    backgroundColor: COLORS.card,
    borderRadius: '12px',
    border: `1px solid ${COLORS.border}`,
    overflow: 'hidden'
  }}>
    {/* Admin Header */}
    <div
      onClick={() => toggleAdminExpand(admin.id)}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 20px',
        backgroundColor: `${getRoleColor('admin')}10`,
        cursor: 'pointer'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {admin.managers.length > 0 ? (
          expandedAdmins[admin.id] ? <ChevronDown size={20} color={COLORS.textSecondary} /> : <ChevronRight size={20} color={COLORS.textSecondary} />
        ) : <div style={{ width: 20 }} />}
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          backgroundColor: `${getRoleColor('admin')}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: getRoleColor('admin'),
          fontWeight: '700',
          fontSize: '16px'
        }}>
          {admin.name?.charAt(0).toUpperCase() || 'A'}
        </div>
        <div>
          <div style={{ color: COLORS.text, fontSize: '15px', fontWeight: '600' }}>
            {admin.name || admin.email}
          </div>
          <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
            {admin.email}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        {admin.subordinate_admins?.length > 0 && (
          <StatItem value={admin.subordinate_admins.length} label="подч. админов" color="#8b5cf6" />
        )}
        <StatItem value={admin.manager_count || admin.managers?.length || 0} label="менеджеров" color="#3b82f6" />
        <StatItem value={admin.client_count || 0} label="в сети" color="#10b981" />
        <StatItem value={admin.direct_client_count || 0} label="своих" color={COLORS.text} />
        <StatItem value={admin.clients_on_managers || 0} label="на менедж." color={COLORS.text} />
        {isSuperAdmin && (
          <ActionButtons 
            onEdit={() => setEditingMember(admin)}
            onDelete={() => setDeletingMember(admin)}
          />
        )}
      </div>
    </div>

    {/* Managers under this admin */}
    {expandedAdmins[admin.id] && admin.managers.length > 0 && (
      <ManagersList 
        managers={admin.managers}
        isSuperAdmin={isSuperAdmin}
        setEditingMember={setEditingMember}
        setDeletingMember={setDeletingMember}
      />
    )}
    
    {/* Subordinate admins */}
    {expandedAdmins[admin.id] && admin.subordinate_admins?.length > 0 && (
      <SubordinateAdminsList 
        admins={admin.subordinate_admins}
        depth={1}
        expandedAdmins={expandedAdmins}
        toggleAdminExpand={toggleAdminExpand}
        isSuperAdmin={isSuperAdmin}
        setEditingMember={setEditingMember}
        setDeletingMember={setDeletingMember}
      />
    )}
  </div>
);

// Helper Components
const StatItem = ({ value, label, color }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ color, fontSize: '18px', fontWeight: '700' }}>{value}</div>
    <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>{label}</div>
  </div>
);

const ActionButtons = ({ onEdit, onDelete }) => (
  <div style={{ display: 'flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
    <button
      onClick={onEdit}
      style={{
        padding: '8px',
        backgroundColor: COLORS.background,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '6px',
        color: COLORS.textSecondary,
        cursor: 'pointer'
      }}
    >
      <Edit2 size={14} />
    </button>
    <button
      onClick={onDelete}
      style={{
        padding: '8px',
        backgroundColor: `${COLORS.danger}15`,
        border: `1px solid ${COLORS.danger}40`,
        borderRadius: '6px',
        color: COLORS.danger,
        cursor: 'pointer'
      }}
    >
      <Trash2 size={14} />
    </button>
  </div>
);

const ManagersList = ({ managers, isSuperAdmin, setEditingMember, setDeletingMember }) => (
  <div style={{ borderTop: `1px solid ${COLORS.border}` }}>
    {managers.map(manager => (
      <div
        key={manager.id}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px 12px 60px',
          borderBottom: `1px solid ${COLORS.border}20`
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: `${getRoleColor('manager')}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: getRoleColor('manager'),
            fontWeight: '600',
            fontSize: '14px'
          }}>
            {manager.name?.charAt(0).toUpperCase() || 'M'}
          </div>
          <div>
            <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '500' }}>
              {manager.name || manager.email}
            </div>
            <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
              {manager.email}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ color: COLORS.text, fontSize: '15px', fontWeight: '600' }}>
            {manager.client_count || 0} клиентов
          </div>
          {isSuperAdmin && (
            <ActionButtons 
              onEdit={() => setEditingMember(manager)}
              onDelete={() => setDeletingMember(manager)}
            />
          )}
        </div>
      </div>
    ))}
  </div>
);

const UnassignedManagersCard = ({ managers, isSuperAdmin, setEditingMember, setDeletingMember }) => (
  <div style={{
    backgroundColor: COLORS.card,
    borderRadius: '12px',
    border: `1px solid ${COLORS.border}`,
    overflow: 'hidden'
  }}>
    <div style={{
      padding: '14px 20px',
      backgroundColor: COLORS.background,
      borderBottom: `1px solid ${COLORS.border}`
    }}>
      <span style={{ color: COLORS.textSecondary, fontSize: '13px', fontWeight: '600' }}>
        МЕНЕДЖЕРЫ БЕЗ ПРИВЯЗКИ ({managers.length})
      </span>
    </div>
    {managers.map(manager => (
      <div
        key={manager.id}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px',
          borderBottom: `1px solid ${COLORS.border}20`
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: `${getRoleColor('manager')}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: getRoleColor('manager'),
            fontWeight: '600'
          }}>
            {manager.name?.charAt(0).toUpperCase() || 'M'}
          </div>
          <div>
            <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '500' }}>
              {manager.name || manager.email}
            </div>
            <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
              {manager.email}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{
            padding: '4px 10px',
            borderRadius: '20px',
            backgroundColor: `${COLORS.danger}20`,
            color: COLORS.danger,
            fontSize: '11px',
            fontWeight: '600'
          }}>
            Не привязан
          </span>
          <div style={{ color: COLORS.text, fontSize: '14px' }}>
            {manager.client_count || 0} клиентов
          </div>
          {isSuperAdmin && (
            <ActionButtons 
              onEdit={() => setEditingMember(manager)}
              onDelete={() => setDeletingMember(manager)}
            />
          )}
        </div>
      </div>
    ))}
  </div>
);

const SupportTeamCard = ({ support, isSuperAdmin, setEditingMember, setDeletingMember }) => (
  <div style={{
    backgroundColor: COLORS.card,
    borderRadius: '12px',
    border: `1px solid ${COLORS.border}`,
    overflow: 'hidden'
  }}>
    <div style={{
      padding: '14px 20px',
      backgroundColor: `${getRoleColor('support')}10`,
      borderBottom: `1px solid ${COLORS.border}`
    }}>
      <span style={{ color: getRoleColor('support'), fontSize: '13px', fontWeight: '600' }}>
        КОМАНДА ПОДДЕРЖКИ ({support.length})
      </span>
    </div>
    {support.map(s => (
      <div
        key={s.id}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px',
          borderBottom: `1px solid ${COLORS.border}20`
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: `${getRoleColor('support')}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: getRoleColor('support'),
            fontWeight: '600'
          }}>
            {s.name?.charAt(0).toUpperCase() || 'S'}
          </div>
          <div>
            <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '500' }}>
              {s.name || s.email}
            </div>
            <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
              {s.email}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{
            padding: '4px 10px',
            borderRadius: '20px',
            backgroundColor: s.is_active !== false ? `${COLORS.success}20` : `${COLORS.danger}20`,
            color: s.is_active !== false ? COLORS.success : COLORS.danger,
            fontSize: '11px',
            fontWeight: '600'
          }}>
            {s.is_active !== false ? 'Активен' : 'Неактивен'}
          </span>
          {isSuperAdmin && (
            <ActionButtons 
              onEdit={() => setEditingMember(s)}
              onDelete={() => setDeletingMember(s)}
            />
          )}
        </div>
      </div>
    ))}
  </div>
);

export default AdminTeam;
